import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, XCircle, Minus } from 'lucide-react';
import type { PersonSummary } from '@/lib/api/people';
import { formatSourceLabel } from '@/lib/sourceLabel';

interface PeopleTableProps {
  people: PersonSummary[];
  isLoading: boolean;
  offset: number;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function QualificationBadge({ person }: { person: PersonSummary }) {
  const status = person.updated_qualification_status || person.qualification_status;
  if (status === 'qualified') {
    return (
      <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" />
        Qualified
      </Badge>
    );
  }
  if (status === 'unqualified') {
    return (
      <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
        <XCircle className="h-3 w-3" />
        Unqualified
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
      <Minus className="h-3 w-3" />
      Unknown
    </Badge>
  );
}

function IntentBadge({ intent }: { intent: string | null | undefined }) {
  if (intent === 'high') {
    return (
      <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">
        High
      </Badge>
    );
  }
  if (intent === 'medium') {
    return (
      <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400">
        Medium
      </Badge>
    );
  }
  if (intent === 'low') {
    return (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
        Low
      </Badge>
    );
  }
  return <span className="text-muted-foreground text-sm">—</span>;
}

const PeopleTable: React.FC<PeopleTableProps> = ({ people, isLoading, offset }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <p className="text-sm">No people yet.</p>
      </div>
    );
  }

  return (
    <Table aria-label="People analytics table">
      <TableCaption className="sr-only">
        List of people with engagement, qualification, and attribution details.
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12 text-center">#</TableHead>
          <TableHead>Person</TableHead>
          <TableHead>Last Seen</TableHead>
          <TableHead className="text-center">Demos</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Time Spent</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Qualification</TableHead>
          <TableHead>Intent</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {people.map((person, index) => (
          <TableRow
            key={person.id}
            className="cursor-pointer hover:bg-accent/50"
            onClick={() => navigate(`/people/${encodeURIComponent(person.email)}`)}
          >
            <TableCell className="text-center text-muted-foreground text-sm">
              {offset + index + 1}
            </TableCell>
            <TableCell className="min-w-[220px]">
              {person.name ? (
                <div className="space-y-0.5">
                  <div className="font-medium text-heading">{person.name}</div>
                  <div className="text-sm text-muted-foreground">{person.email}</div>
                  {person.business && (
                    <div className="text-xs text-muted-foreground">
                      Company · {person.business}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-0.5">
                  <div className="font-medium text-heading">{person.email}</div>
                  {person.business && (
                    <div className="text-xs text-muted-foreground">
                      Company · {person.business}
                    </div>
                  )}
                </div>
              )}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDate(person.last_seen_at)}
            </TableCell>
            <TableCell className="text-center text-sm">
              {person.total_sessions}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatSourceLabel(person.last_source)}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {formatDuration(person.total_time_spent_seconds)}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {person.last_location || '—'}
            </TableCell>
            <TableCell>
              <QualificationBadge person={person} />
            </TableCell>
            <TableCell>
              <IntentBadge intent={person.buying_intent} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default PeopleTable;
